import { observer } from 'mobx-react';
import React, { ChangeEvent, useCallback, useMemo } from 'react';
import dealsStore from '../../store/dealsStore';
import { localeStore } from 'src/mobx/localesStore';
import { booleanNumberReverse, getLastArrayItem, getTranslate, stringTransform } from 'src/utils/helpers';
import { numberValidation } from 'src/utils/validation';
import InputAlt from 'src/view/components/Controls/InputAlt/InputAlt';
import MultiLanguageInput from 'src/view/components/Controls/MultiLanguageInput/MultiLanguageInput';
import MultiLanguageTextArea from 'src/view/components/Controls/MultiLanguageTextArea/MultiLanguageTextArea';
import LazyCategoriesPicker from 'src/view/components/Controls/Pickers/LazyCategoriesPicker/LazyCategoriesPicker';
import { IPickerOption } from 'src/view/components/Controls/Pickers/Picker/types';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import Text, { TEXT } from 'src/view/components/Text/Text';
import { ProductWarning } from '~/view/components/ProductWarning';
import './style.scss';

const DealsMainDetails: React.FC = observer((): JSX.Element | null => {
  const selectedCategories = useMemo((): IPickerOption[] | undefined => {
    const { deal: _deal } = dealsStore;
    if (!_deal?.category) return undefined;

    return [{ label: getTranslate(_deal.category.name), value: _deal.category.id, status: _deal.category.status }];
  }, [dealsStore.deal]);

  const selectCategory = useCallback((options: IPickerOption[]) => {
    const option: IPickerOption | undefined = getLastArrayItem<IPickerOption>(options);
    dealsStore.changeDealHandler({
      category: option ? { id: Number(option.value), name: { [localeStore.language]: option.label }, status: option.status } : undefined,
    });
    // update validation
    if (!dealsStore.validation.category) dealsStore.setValidation();
  }, []);

  const onSortNumberChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (!numberValidation(value)) return;
    dealsStore.changeDealHandler({ sort: stringTransform(value) });
  }, []);

  const { deal } = dealsStore;
  if (!deal) return null;

  return (
    <div className="d-flex flex-column mt-4">
      {/* switchers */}
      <div className="d-flex flex-row">
        <div className="d-flex flex-row align-items-center mr-8">
          <Switcher status={deal?.status || 0} onChange={(status) => dealsStore.changeDealHandler({ status })} />
          <Text type={TEXT.SWITCHER_LABEL} text="availability" />
        </div>
        <div className="d-flex flex-row align-items-center mr-8">
          <Switcher
            status={booleanNumberReverse(deal?.out_of_stock)}
            onChange={(stock) => dealsStore.changeDealHandler({ out_of_stock: booleanNumberReverse(stock) })}
          />
          <Text type={TEXT.SWITCHER_LABEL} text="in_stock" />
        </div>
      </div>
      {/* main details */}
      <Text type={TEXT.SECTION_HEADER} text="main_details" className="mt-6" />
      <div className="d-flex flex-row mt-1">
        <div className="d-flex flex-column mr-3">
          {/* name */}
          <Text type={TEXT.BLOCK_HEADER} text="combo_offer_name" className="deals-main-details__name-title" required />
          <MultiLanguageInput
            maxLength={75}
            enText={deal.name.en}
            arText={deal.name.ar}
            onChangeValue={dealsStore.changeDealName}
            validation={dealsStore.validation.name}
            className="mt-2"
          />
          {/* category */}
          <div className="d-flex flex-row mt-5">
            <Text type={TEXT.BLOCK_HEADER} text="category" className="mb-2" required />
            <ProductWarning type="categoryInEditPage" combo={deal} iconSize={21} marginLeft={2} />
          </div>
          <LazyCategoriesPicker
            searchable
            markUnavailable
            validation={{ isValid: dealsStore.validation.category, errorText: dealsStore.validation.categoryErrorText }}
            selectedOptions={selectedCategories}
            onSelect={selectCategory}
            className="deals-main-details__input"
          />
        </div>
        {/* descriptions */}
        <MultiLanguageTextArea
          title="description"
          enText={deal.description?.en}
          arText={deal.description?.ar}
          onChangeValue={dealsStore.changeDealDescription}
          className="deals-main-details__text-area h-auto"
        />
      </div>
      <div className="d-flex flex-column mt-5">
        <Text type={TEXT.BLOCK_HEADER} text="sort" />
        <InputAlt className="deals-main-details__input py-0 mt-2" value={deal.sort} onChange={onSortNumberChange} />
      </div>
    </div>
  );
});

export default DealsMainDetails;
